import { HGTimeRecordPlus } from "./types";

/**
 * Folding a collection's records into the record that heads it.
 *
 * A collection may name one of the user's records as its **title record** — the
 * record that *is* that collection. "The Second Punic War" is both an event on
 * the timeline and somewhere to file the forty battles inside it.
 *
 * Away from that collection's own page, listing the war and then all forty
 * battles beside it says the same thing twice. So a title record with a span
 * stands in for whatever of its collection falls inside that span: those records
 * leave the list, and the record's row carries a way into the collection
 * instead. Records of the same collection that fall *outside* the span are
 * unaffected — they are not part of what the record describes.
 *
 * This is deliberately a filter over the record list rather than a step inside
 * `buildRecordTree`: the tree knows about intervals, not collections, and
 * keeping it that way means this feature adds no options, no modes and no
 * branches to the timeline's own logic.
 */

function duration(record: HGTimeRecordPlus): bigint {
  if (record.end_second === null) return 0n;
  return record.end_second - record.start_second;
}

/**
 * What the tree is built from: everything not folded away.
 *
 * @param records         the loaded records for the current viewport
 * @param descendantsOf   collection id → itself and everything below it, from
 *                        `buildDescendantsMap`
 * @param activeCollectionID  the collection being browsed, if any
 */
export function applyTitleRecords(
  records: HGTimeRecordPlus[],
  descendantsOf: Map<string, Set<string>>,
  activeCollectionID?: string,
): HGTimeRecordPlus[] {
  // A title record only folds a record away when it has a span to fold it into:
  // an open-ended record covers no definite stretch of time, and the record's
  // own collection page shows everything, which is what makes anything hidden
  // here reachable.
  const candidates = records.filter(
    (record) =>
      !record.external &&
      !!record.title_collection_id &&
      record.end_second !== null &&
      record.title_collection_id !== activeCollectionID &&
      descendantsOf.has(record.title_collection_id),
  );

  if (candidates.length === 0) return records;

  // Longest span first, id as tiebreak. Two things follow from this order, and
  // both matter:
  //
  //  - the *outermost* title record claims a record, so a title record nested
  //    inside another one is folded away along with everything it would have
  //    claimed, and nothing is counted twice;
  //  - because the ordering is total and the quantity strictly shrinks, a
  //    candidate can never be folded into something it folds, so the pass
  //    terminates and gives the same answer on every rebuild.
  const hiders = [...candidates].sort((a, b) => {
    const da = duration(a);
    const db = duration(b);
    if (da !== db) return da < db ? 1 : -1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  const covers = (hider: HGTimeRecordPlus, record: HGTimeRecordPlus) => {
    if (record.id === hider.id) return false;
    // External rows neither fold nor are folded. Folding is a promise that the
    // hidden record is still reachable on its collection's own page, and a row
    // fetched live for *this* interval is not: drilling into the title record
    // changes the range, and the provider would be asked a different question.
    // A count row is doubly unfoldable -- it stands for records, and hiding it
    // would hide a number that is already the summary of what is there.
    if (record.external) return false;
    // An ongoing record does not "fit inside" anything.
    if (record.end_second === null) return false;
    if (record.start_second < hider.start_second) return false;
    if (record.end_second > (hider.end_second as bigint)) return false;
    const subtree = descendantsOf.get(hider.title_collection_id as string);
    if (!subtree) return false;
    // Any match: a record filed in the subtree folds away even when it is also
    // filed somewhere unrelated. Its own collection page still lists it.
    return record.collection_id.some((id) => subtree.has(id));
  };

  // A set, not "record id → the hider that claimed it": which title record a
  // record folded into is no longer shown anywhere, and only membership decides
  // both what leaves the list and whether a hider is itself already folded.
  const hidden = new Set<string>();

  for (const hider of hiders) {
    // A hider that has itself been folded away is a back-reference, not a
    // second home: whatever it would claim already belongs to whatever claimed
    // it, which is a superset of this collection's subtree.
    if (hidden.has(hider.id)) continue;
    for (const record of records) {
      if (hidden.has(record.id)) continue;
      if (covers(hider, record)) hidden.add(record.id);
    }
  }

  if (hidden.size === 0) return records;

  return records.filter((record) => !hidden.has(record.id));
}
